(() => {
  console.log("[store-kiosk] loaded (page-per-store)");

  const IDLE_EVENT_TYPES = ["click", "touchstart", "keydown", "input"];

  const $ = (root, sel) => (root || document).querySelector(sel);
  const $$ = (root, sel) => Array.from((root || document).querySelectorAll(sel));

  function clampInt(n, min, max, fallback) {
    const x = parseInt(n, 10);
    if (Number.isNaN(x)) return fallback;
    return Math.max(min, Math.min(max, x));
  }

  function readBoolAttr(el, name, fallback = false) {
    if (!el) return fallback;
    const v = el.getAttribute(name);
    if (v == null) return fallback;
    return v === "1" || v === "true";
  }

  // -------------------------
  // Visual toggles
  // -------------------------
  function applyGlobalTexture(section, enableTexture) {
    section.classList.toggle("kiosk--texture", !!enableTexture);
  }

  function applyPriorityGlow(section, enableGlow) {
    if (!enableGlow) {
      section.classList.remove("kiosk--priority");
      return;
    }
    const isPriority = section.getAttribute("data-priority-store") === "1";
    section.classList.toggle("kiosk--priority", !!isPriority);
  }

  function applyReveal(section, enableReveal, minimalMotion) {
    const targets = $$(section, "[data-reveal]");

    if (minimalMotion) {
      section.classList.add("kiosk--reduced");
      section.classList.remove("kiosk--reveal");

      // Ensure everything is visible
      targets.forEach((el) => {
        el.classList.remove("is-hidden");
        el.classList.add("is-shown");
      });
      return;
    }

    section.classList.remove("kiosk--reduced");
    section.classList.toggle("kiosk--reveal", !!enableReveal);

    if (!enableReveal) {
      targets.forEach((el) => {
        el.classList.remove("is-hidden");
        el.classList.remove("is-shown");
      });
      return;
    }

    // Start hidden (CSS animates into view)
    targets.forEach((el) => {
      el.classList.add("is-hidden");
      el.classList.remove("is-shown");
    });

    // Intersection observer for smooth reveal
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((ent) => {
          if (ent.isIntersecting) {
            ent.target.classList.add("is-shown");
            ent.target.classList.remove("is-hidden");
            io.unobserve(ent.target);
          }
        });
      },
      { root: null, threshold: 0.12 }
    );

    targets.forEach((t) => io.observe(t));
  }

  // -------------------------
  // Widget wiring (page-per-store)
  // Liquid sets store_code/outlet_id on the widget.
  // We mirror those values to the section for CSS / glow.
  // -------------------------
  function mirrorWidgetStoreToSection(section) {
    const widget = $(section, "[data-store-stock]");
    if (!widget) return;

    const storeCode = (widget.getAttribute("data-store-code") || "").trim();
    const outletId = (widget.getAttribute("data-outlet-id") || "").trim();
    const isPriority = (widget.getAttribute("data-priority-store") || "0").trim();

    // Mirror to section so this file can read consistently
    section.setAttribute("data-store-code", storeCode);
    section.setAttribute("data-outlet-id", outletId);
    section.setAttribute("data-priority-store", isPriority === "1" ? "1" : "0");

    // If the badge is still the placeholder, give it a generic fallback
    const badge = $(section, "[data-active-store]");
    if (badge && badge.textContent && badge.textContent.trim() === "Loading store…") {
      badge.textContent = "Store";
    }

    if (!storeCode && !outletId) {
      console.warn("[store-kiosk] Missing store_code/outlet_id on widget. Check metaobject fields in Liquid.");
    }
  }

  function applySuggestToggle(section, enableSuggest) {
    // store-location-stock.js should respect this attr
    const widgets = $$(section, "[data-store-stock]");
    widgets.forEach((w) => w.setAttribute("data-suggest-enabled", enableSuggest ? "1" : "0"));
  }

  // -------------------------
  // Idle reset
  // -------------------------
  function setupIdleReset(section, enabled, minutes) {
    if (!enabled) return;

    const timeoutMs = clampInt(minutes, 1, 60, 5) * 60 * 1000;
    let timer = null;

    const resetUI = () => {
      const input = $(section, "[data-store-stock-query]");
      const results = $(section, "[data-store-stock-results]");
      const meta = $(section, "[data-store-stock-meta]");
      const suggest = $(section, "[data-store-stock-suggest]");
      const variantWrap = $(section, "[data-store-stock-variant]");
      const variantSelect = $(section, "[data-store-stock-variant-select]");

      if (input) input.value = "";
      if (results) results.innerHTML = "";
      if (meta) meta.textContent = "";

      if (suggest) {
        suggest.innerHTML = "";
        suggest.hidden = true;
      }

      if (variantSelect) variantSelect.innerHTML = "";
      if (variantWrap) {
        variantWrap.hidden = true;
        variantWrap.setAttribute("data-variant-ready", "0");
      }

      console.log("[store-kiosk] idle reset");
    };

    const startTimer = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(resetUI, timeoutMs);
    };

    const activityHandler = () => startTimer();

    IDLE_EVENT_TYPES.forEach((evt) => section.addEventListener(evt, activityHandler, true));
    startTimer();
  }

  // -------------------------
  // Init
  // -------------------------
  function initSection(section) {
    // Read toggles from attributes set in Liquid
    const enableTexture = readBoolAttr(section, "data-enable-texture", false);
    const enablePriorityGlow = readBoolAttr(section, "data-enable-priority-glow", true);
    const enableReveal = readBoolAttr(section, "data-enable-reveal", true);
    const minimalMotion = readBoolAttr(section, "data-minimal-motion", false);

    const enableIdleReset = readBoolAttr(section, "data-enable-idle-reset", true);
    const idleMinutes = section.getAttribute("data-idle-minutes") || "5";

    const enableSuggest = readBoolAttr(section, "data-enable-suggest", true);

    // Mirror widget attrs -> section attrs (do NOT overwrite widget)
    mirrorWidgetStoreToSection(section);

    // Apply visuals
    applyGlobalTexture(section, enableTexture);
    applyPriorityGlow(section, enablePriorityGlow);
    applyReveal(section, enableReveal, minimalMotion);

    // Behaviour
    applySuggestToggle(section, enableSuggest);
    setupIdleReset(section, enableIdleReset, idleMinutes);
  }

  function init() {
    $$(document, ".store-kiosk").forEach((section) => {
      if (section.dataset.kioskWired === "1") return;
      section.dataset.kioskWired = "1";
      initSection(section);
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  // Theme editor / dynamic section rerender
  document.addEventListener("shopify:section:load", () => {
    $$(document, ".store-kiosk").forEach((s) => (s.dataset.kioskWired = "0"));
    init();
  });
})();

function initKioskPin() {
  if (!document.body.classList.contains("kiosk-layout")) return;

  // Prevent double-wiring
  if (document.body.dataset.kioskPinWired === "1") return;
  document.body.dataset.kioskPinWired = "1";

  const PIN = "6969";

  // Match your actual kiosk escape button (likely an <a>)
  const btn = document.querySelector(".kiosk-escape__home");
  if (!btn) return;

  // Use existing overlay if present, otherwise create it (safe + future-proof)
  let modal = document.querySelector(".kiosk-pin");
  if (!modal) {
    modal = document.createElement("div");
    modal.className = "kiosk-pin";
    modal.hidden = true;
    modal.innerHTML = `
      <div class="kiosk-pin__panel" role="dialog" aria-modal="true" aria-label="Enter PIN">
        <div class="kiosk-pin__title">Enter PIN to exit kiosk</div>
        <input class="kiosk-pin__input" type="password" inputmode="numeric" autocomplete="one-time-code" placeholder="PIN" />
        <div class="kiosk-pin__error" aria-live="polite"></div>
        <div class="kiosk-pin__actions">
          <button type="button" data-kiosk-pin-cancel>Cancel</button>
          <button type="button" data-kiosk-pin-submit>Unlock</button>
        </div>
      </div>
    `;
    document.body.appendChild(modal);
  }

  const input = modal.querySelector(".kiosk-pin__input");
  const error = modal.querySelector(".kiosk-pin__error");
  const cancel = modal.querySelector("[data-kiosk-pin-cancel]");
  const submit = modal.querySelector("[data-kiosk-pin-submit]");

  if (!input) return;

  // Ensure hidden at startup (now modal exists)
  modal.hidden = true;

  function open(e) {
    // stop the <a href="/"> escape from navigating immediately
    e?.preventDefault?.();
    e?.stopPropagation?.();

    modal.hidden = false;
    input.value = "";
    if (error) error.textContent = "";
    setTimeout(() => input.focus(), 50);
  }

  function close() {
    modal.hidden = true;
    input.value = "";
    if (error) error.textContent = "";
  }

  function check() {
    if ((input.value || "").trim() === PIN) {
      // go where the escape button wanted to go
      const href = btn.getAttribute("href") || "/";
      window.location.href = href;
      return;
    }
    if (error) error.textContent = "Incorrect PIN";
    input.value = "";
    input.focus();
  }

  // Backdrop click closes (so you never brick the kiosk again)
  modal.addEventListener("click", (e) => {
    const panel = modal.querySelector(".kiosk-pin__panel");
    if (panel && !panel.contains(e.target)) close();
  });

  btn.addEventListener("click", open, true);
  cancel?.addEventListener("click", (e) => { e.preventDefault(); e.stopPropagation(); close(); }, true);
  submit?.addEventListener("click", (e) => { e.preventDefault(); e.stopPropagation(); check(); }, true);

  input.addEventListener("keydown", (e) => {
    if (e.key === "Enter") { e.preventDefault(); check(); }
    if (e.key === "Escape") { e.preventDefault(); close(); }
  });
}

// Run at the right time
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initKioskPin);
} else {
  initKioskPin();
}

